/**
 * ComponentIcons — draws detailed mini-icons for each component type
 * using Phaser Graphics primitives. Used by WorkshopScene grid and palette.
 */
import Phaser from 'phaser';
import { ComponentType, COMPONENT_DEFS } from '@/components/ComponentDefs';
import { ProgressionManager } from '@/systems/ProgressionManager';
import { getOverride } from '@/config/ComponentOverrides';
import { makeRenderTexture } from '@/utils/PhaserHelpers';

/** Draw a component icon centered at (0,0) on the given Graphics object. */
export function drawComponentIcon(
  gfx: Phaser.GameObjects.Graphics,
  type: ComponentType,
  size: number,
  color: number,
): void {
  const s = size;
  const hs = s / 2;
  // Scale factor relative to reference size (46px workshop icon)
  const k = s / 46;
  const lw = Math.max(1, k);  // line width, min 1px

  switch (type) {
    // ── Structure: metal plate with corner bolts ──
    case ComponentType.STRUCTURE: {
      const pad = 2 * k;
      const rad = 3 * k;
      gfx.fillStyle(color, 0.9);
      gfx.fillRoundedRect(-hs + pad, -hs + pad, s - pad * 2, s - pad * 2, rad);
      gfx.lineStyle(lw, 0xffffff, 0.15);
      gfx.strokeRoundedRect(-hs + pad, -hs + pad, s - pad * 2, s - pad * 2, rad);
      // corner bolts
      const b = hs * 0.7;
      const br = 2 * k;
      gfx.fillStyle(0xaaaaaa, 0.6);
      for (const [bx, by] of [[-b, -b], [b, -b], [-b, b], [b, b]]) {
        gfx.fillCircle(bx, by, br);
      }
      break;
    }

    // ── Armor: thick plate with cross-hatch ──
    case ComponentType.ARMOR: {
      const pad = 2 * k;
      const rad = 2 * k;
      gfx.fillStyle(color, 0.95);
      gfx.fillRoundedRect(-hs + pad, -hs + pad, s - pad * 2, s - pad * 2, rad);
      gfx.lineStyle(2 * k, 0x5588bb, 0.3);
      gfx.strokeRoundedRect(-hs + pad * 1.5, -hs + pad * 1.5, s - pad * 3, s - pad * 3, rad);
      // cross hatch lines
      gfx.lineStyle(lw, 0x4477aa, 0.2);
      const hStep = 6 * k;
      for (let i = -hs + hStep * 1.3; i < hs; i += hStep) {
        gfx.lineBetween(i, -hs + pad * 2, i, hs - pad * 2);
      }
      break;
    }

    // ── Battery: yellow block with lightning bolt ──
    case ComponentType.BATTERY: {
      const px = 2 * k;
      const py = 4 * k;
      const rad = 3 * k;
      gfx.fillStyle(color, 0.9);
      gfx.fillRoundedRect(-hs + px, -hs + py, s - px * 2, s - py * 2, rad);
      // Lightning bolt
      const bw = 3 * k;
      const bh = hs - py * 1.5;
      gfx.fillStyle(0xffffff, 0.8);
      gfx.fillTriangle(-bw, -bh, bw * 0.7, 0, -bw * 0.3, 0);
      gfx.fillTriangle(bw * 0.3, 0, -bw * 0.7, 0, bw, bh);
      break;
    }

    // ── Wheel: circle with spokes ──
    case ComponentType.WHEEL: {
      const r = hs * 0.82;
      gfx.fillStyle(0x111111, 1);
      gfx.fillCircle(0, 0, r + lw);
      gfx.fillStyle(color, 0.9);
      gfx.fillCircle(0, 0, r);
      gfx.lineStyle(2 * k, 0x444444, 0.8);
      gfx.strokeCircle(0, 0, r);
      gfx.fillStyle(0x555555, 0.9);
      gfx.fillCircle(0, 0, r * 0.35);
      gfx.lineStyle(lw, 0x444444, 0.5);
      for (let a = 0; a < Math.PI * 2; a += Math.PI / 3) {
        gfx.lineBetween(
          Math.cos(a) * r * 0.35, Math.sin(a) * r * 0.35,
          Math.cos(a) * r * 0.85, Math.sin(a) * r * 0.85,
        );
      }
      break;
    }

    // ── Thruster: nozzle with flame ──
    case ComponentType.THRUSTER: {
      const bh = s * 0.26;
      gfx.fillStyle(0x555555, 0.9);
      gfx.fillRect(-hs * 0.8, -bh, s * 0.5, bh * 2);
      gfx.fillStyle(color, 0.95);
      gfx.fillTriangle(hs * 0.7, -bh * 1.3, hs * 0.7, bh * 1.3, hs * 0.95, 0);
      // Flame
      gfx.fillStyle(0xff4400, 0.7);
      gfx.fillTriangle(-hs * 0.8, -bh * 0.7, -hs * 0.8, bh * 0.7, -hs * 1.05, 0);
      gfx.fillStyle(0xffaa00, 0.5);
      gfx.fillTriangle(-hs * 0.8, -bh * 0.35, -hs * 0.8, bh * 0.35, -hs * 1.15, 0);
      break;
    }

    // ── Sensor Short: small antenna ──
    case ComponentType.SENSOR_SHORT: {
      const r = hs * 0.8;
      gfx.fillStyle(color, 0.9);
      gfx.fillCircle(0, 0, r);
      gfx.lineStyle(lw, 0xffffff, 0.3);
      gfx.strokeCircle(0, 0, r);
      gfx.fillStyle(0xffffff, 0.8);
      gfx.fillCircle(0, -r * 0.85, 2 * k);
      gfx.lineStyle(lw, color, 0.3);
      gfx.strokeCircle(0, 0, hs * 0.95);
      break;
    }

    // ── Sensor Medium: radar dish ──
    case ComponentType.SENSOR_MEDIUM: {
      const r = hs * 0.85;
      gfx.fillStyle(color, 0.9);
      gfx.fillCircle(0, 0, r);
      gfx.lineStyle(1.5 * k, 0x44ff44, 0.4);
      gfx.beginPath();
      gfx.arc(0, 0, r * 0.8, -0.8, 0.8, false);
      gfx.strokePath();
      gfx.fillStyle(0x66ff66, 0.9);
      gfx.fillCircle(0, 0, 2.5 * k);
      gfx.lineStyle(lw, color, 0.4);
      gfx.strokeCircle(0, 0, hs * 0.95);
      break;
    }

    // ── Cannon Light: small barrel ──
    case ComponentType.CANNON_LIGHT: {
      const bh = s * 0.22;
      gfx.fillStyle(0x444444, 0.9);
      gfx.fillRect(-hs * 0.8, -bh, s * 0.4, bh * 2);
      gfx.fillStyle(color, 0.95);
      gfx.fillRect(0, -bh * 0.6, hs * 0.85, bh * 1.2);
      gfx.fillStyle(0xff6644, 0.6);
      gfx.fillCircle(hs * 0.85, 0, 2 * k);
      break;
    }

    // ── Cannon Heavy: large barrel ──
    case ComponentType.CANNON_HEAVY: {
      const bh = s * 0.3;
      gfx.fillStyle(0x333333, 0.9);
      gfx.fillRect(-hs * 0.85, -bh, s * 0.4, bh * 2);
      gfx.fillStyle(color, 0.95);
      gfx.fillRect(-s * 0.04, -bh * 0.7, hs * 0.9, bh * 1.4);
      gfx.fillStyle(0x666666, 0.7);
      gfx.fillRect(hs * 0.7, -bh * 0.85, 4 * k, bh * 1.7);
      break;
    }

    // ── Burst Cannon: triple barrel ──
    case ComponentType.BURST_CANNON: {
      const bh = s * 0.3;
      gfx.fillStyle(0x444444, 0.9);
      gfx.fillRect(-hs * 0.8, -bh, s * 0.35, bh * 2);
      gfx.fillStyle(color, 0.95);
      const barH = s * 0.065;
      for (const yOff of [-s * 0.17, 0, s * 0.17]) {
        gfx.fillRect(0, yOff - barH, hs * 0.75, barH * 2);
      }
      break;
    }

    // ── Explosive Charge: bomb ──
    case ComponentType.EXPLOSIVE_CHARGE: {
      const r = hs * 0.75;
      gfx.fillStyle(color, 0.95);
      gfx.fillCircle(0, s * 0.04, r);
      gfx.lineStyle(1.5 * k, 0x888888, 0.8);
      gfx.lineBetween(0, -r * 0.7, 3 * k, -r * 1.1);
      gfx.fillStyle(0xffff00, 0.9);
      gfx.fillCircle(3 * k, -r * 1.1, 2 * k);
      gfx.lineStyle(lw, 0x000000, 0.3);
      gfx.lineBetween(-r * 0.5, 0, r * 0.5, 0);
      break;
    }

    // ── Repair Module: wrench/cross ──
    case ComponentType.REPAIR_MODULE: {
      const pad = 3 * k;
      gfx.fillStyle(color, 0.85);
      gfx.fillRoundedRect(-hs + pad, -hs + pad, s - pad * 2, s - pad * 2, 3 * k);
      const cw = s * 0.09;
      const ch = s * 0.3;
      gfx.fillStyle(0xffffff, 0.7);
      gfx.fillRect(-cw, -ch, cw * 2, ch * 2);
      gfx.fillRect(-ch, -cw, ch * 2, cw * 2);
      break;
    }

    // ── Recharge Module: battery + arrow ──
    case ComponentType.RECHARGE_MODULE: {
      const pad = 3 * k;
      gfx.fillStyle(color, 0.85);
      gfx.fillRoundedRect(-hs + pad, -hs + pad, s - pad * 2, s - pad * 2, 3 * k);
      const aw = s * 0.22;
      const ah = s * 0.15;
      gfx.fillStyle(0xffcc00, 0.8);
      gfx.fillTriangle(0, -ah * 2.2, -aw, -ah * 0.3, aw, -ah * 0.3);
      gfx.fillRect(-aw * 0.4, -ah * 0.3, aw * 0.8, ah * 2.5);
      break;
    }

    // ── Wing: wing silhouette ──
    case ComponentType.WING: {
      const wx = hs * 0.85;
      gfx.fillStyle(color, 0.9);
      gfx.fillTriangle(-wx, s * 0.09, wx, -s * 0.04, wx, s * 0.13);
      gfx.fillTriangle(-wx, -s * 0.09, wx, s * 0.04, wx, -s * 0.13);
      gfx.fillStyle(0x88bbee, 0.5);
      gfx.fillRect(-s * 0.065, -s * 0.02, s * 0.13, s * 0.04);
      break;
    }

    // ── Missile SAM: vertical rocket ──
    case ComponentType.MISSILE_SAM: {
      const bw = s * 0.13;
      const top = -hs * 0.8;
      const bot = hs * 0.7;
      gfx.fillStyle(color, 0.9);
      gfx.fillRect(-bw, top + s * 0.1, bw * 2, bot - top - s * 0.1);
      gfx.fillTriangle(0, top, -bw, top + s * 0.12, bw, top + s * 0.12);
      gfx.fillStyle(0x2277cc, 0.7);
      const fw = s * 0.09;
      gfx.fillTriangle(-bw, bot - s * 0.07, -bw - fw, bot, -bw, bot);
      gfx.fillTriangle(bw, bot - s * 0.07, bw + fw, bot, bw, bot);
      break;
    }

    // ── Missile Sol-Sol: horizontal rocket ──
    case ComponentType.MISSILE_SOL_SOL: {
      const bh = s * 0.13;
      const left = -hs * 0.75;
      const right = hs * 0.75;
      gfx.fillStyle(color, 0.9);
      gfx.fillRect(left, -bh, right - left - s * 0.1, bh * 2);
      gfx.fillTriangle(right, 0, right - s * 0.1, -bh * 1.3, right - s * 0.1, bh * 1.3);
      gfx.fillStyle(0xaa5511, 0.7);
      const fw = s * 0.09;
      gfx.fillTriangle(left + s * 0.02, -bh, left + s * 0.02, -bh - fw, left + s * 0.1, -bh);
      gfx.fillTriangle(left + s * 0.02, bh, left + s * 0.02, bh + fw, left + s * 0.1, bh);
      gfx.fillStyle(0xff6600, 0.5);
      gfx.fillCircle(left, 0, 2 * k);
      break;
    }

    // ── Structure Triangle: right-angle bracket (équerre) ──
    case ComponentType.STRUCTURE_TRIANGLE: {
      const pad = 2 * k;
      const inner = hs - pad;
      // Right-angle triangle: right angle at bottom-left
      // Vertices: bottom-left, bottom-right, top-left
      gfx.fillStyle(color, 0.9);
      gfx.fillTriangle(-inner, inner, inner, inner, -inner, -inner);
      gfx.lineStyle(lw, 0xffffff, 0.2);
      gfx.lineBetween(-inner, inner, inner, inner);    // bottom edge (leg)
      gfx.lineBetween(-inner, inner, -inner, -inner);  // left edge (leg)
      gfx.lineBetween(-inner, -inner, inner, inner);   // hypotenuse
      // Right-angle marker at bottom-left corner
      const m = 5 * k;
      gfx.lineStyle(lw, 0xffffff, 0.4);
      gfx.lineBetween(-inner + m, inner, -inner + m, inner - m);
      gfx.lineBetween(-inner, inner - m, -inner + m, inner - m);
      // Corner bolts on the two legs
      const br = 1.5 * k;
      gfx.fillStyle(0xaaaaaa, 0.6);
      gfx.fillCircle(-inner + 4 * k, inner - 4 * k, br); // bottom-left corner
      gfx.fillCircle(inner - 4 * k, inner - 4 * k, br);  // bottom-right
      gfx.fillCircle(-inner + 4 * k, -inner + 4 * k, br); // top-left
      break;
    }

    // ── Flamethrower: nozzle with flame cone ──
    case ComponentType.FLAMETHROWER: {
      const bh = s * 0.25;
      // Tank body
      gfx.fillStyle(0x555555, 0.9);
      gfx.fillRect(-hs * 0.8, -bh, s * 0.35, bh * 2);
      // Nozzle
      gfx.fillStyle(0x444444, 0.9);
      gfx.fillRect(-hs * 0.1, -bh * 0.5, hs * 0.5, bh);
      // Flame cone (3 layers: outer orange, mid yellow-orange, inner yellow)
      gfx.fillStyle(0xff4400, 0.5);
      gfx.fillTriangle(hs * 0.3, -bh * 1.4, hs * 0.3, bh * 1.4, hs * 1.0, 0);
      gfx.fillStyle(0xff8800, 0.6);
      gfx.fillTriangle(hs * 0.3, -bh * 0.9, hs * 0.3, bh * 0.9, hs * 0.85, 0);
      gfx.fillStyle(0xffcc00, 0.7);
      gfx.fillTriangle(hs * 0.3, -bh * 0.4, hs * 0.3, bh * 0.4, hs * 0.7, 0);
      break;
    }

    // ── Laser: emitter with beam line ──
    case ComponentType.LASER: {
      const bh = s * 0.2;
      // Housing
      gfx.fillStyle(0x333344, 0.9);
      gfx.fillRect(-hs * 0.8, -bh * 1.2, s * 0.5, bh * 2.4);
      // Lens/emitter crystal
      gfx.fillStyle(color, 0.95);
      gfx.fillRect(-hs * 0.05, -bh * 0.8, hs * 0.4, bh * 1.6);
      // Beam preview line
      gfx.lineStyle(2 * k, 0xff0044, 0.6);
      gfx.lineBetween(hs * 0.35, 0, hs * 0.95, 0);
      // Glow dot at tip
      gfx.fillStyle(0xff0044, 0.9);
      gfx.fillCircle(hs * 0.95, 0, 2 * k);
      // Small glow at lens
      gfx.fillStyle(0xff4488, 0.5);
      gfx.fillCircle(hs * 0.15, 0, 3 * k);
      break;
    }

    // ── Stabilization Legs: crane-style deployable legs ──
    case ComponentType.STAB_LEGS: {
      const pad = 3 * k;
      // Base plate
      gfx.fillStyle(color, 0.8);
      gfx.fillRect(-hs + pad, -hs * 0.3, s - pad * 2, hs * 0.6);
      // Left leg (angled outward)
      gfx.lineStyle(2.5 * k, 0x886633, 0.9);
      gfx.lineBetween(-hs * 0.5, hs * 0.3, -hs * 0.85, hs * 0.85);
      // Right leg (angled outward)
      gfx.lineBetween(hs * 0.5, hs * 0.3, hs * 0.85, hs * 0.85);
      // Foot pads
      gfx.fillStyle(0x665533, 0.9);
      gfx.fillRect(-hs * 0.95, hs * 0.75, hs * 0.3, hs * 0.2);
      gfx.fillRect(hs * 0.65, hs * 0.75, hs * 0.3, hs * 0.2);
      // Center bolt
      gfx.fillStyle(0xaaaaaa, 0.7);
      gfx.fillCircle(0, 0, 2.5 * k);
      break;
    }

    case ComponentType.TURBINE: {
      // Helicopter rotor: central hub + 3 blades
      const hub = 3.5 * k;
      gfx.fillStyle(0x444444, 0.9);
      gfx.fillCircle(0, 0, hub);
      // 3 rotor blades at 120° intervals
      for (let b = 0; b < 3; b++) {
        const angle = (b / 3) * Math.PI * 2 - Math.PI / 2;
        const bladeLen = hs * 0.85;
        const bladeW = 2.5 * k;
        gfx.fillStyle(color, 0.8);
        gfx.fillRect(
          Math.cos(angle) * hub - bladeW / 2,
          Math.sin(angle) * hub - bladeW / 2,
          0, 0,
        );
        // Blade as a line
        gfx.lineStyle(bladeW, color, 0.8);
        gfx.lineBetween(
          Math.cos(angle) * hub, Math.sin(angle) * hub,
          Math.cos(angle) * bladeLen, Math.sin(angle) * bladeLen,
        );
        // Blade tip
        gfx.fillStyle(0xffffff, 0.3);
        gfx.fillCircle(Math.cos(angle) * bladeLen, Math.sin(angle) * bladeLen, 1.5 * k);
      }
      // Center highlight
      gfx.fillStyle(0xaaaaaa, 0.6);
      gfx.fillCircle(0, 0, 1.5 * k);
      break;
    }

    // ── Mine Layer: circle with radiating lines (mine symbol) ──
    case ComponentType.MINE_LAYER: {
      const mineR = s * 0.22;
      // Outer ring
      gfx.lineStyle(lw * 1.5, color, 0.8);
      gfx.strokeCircle(0, 0, mineR);
      // Inner fill
      gfx.fillStyle(color, 0.6);
      gfx.fillCircle(0, 0, mineR * 0.6);
      // Radiating spikes (6 lines)
      gfx.lineStyle(lw, color, 0.7);
      for (let i = 0; i < 6; i++) {
        const angle = (i / 6) * Math.PI * 2;
        const inner = mineR * 0.8;
        const outer = mineR * 1.5;
        gfx.beginPath();
        gfx.moveTo(Math.cos(angle) * inner, Math.sin(angle) * inner);
        gfx.lineTo(Math.cos(angle) * outer, Math.sin(angle) * outer);
        gfx.strokePath();
      }
      // Center dot (detonator)
      gfx.fillStyle(0xff4400, 0.8);
      gfx.fillCircle(0, 0, mineR * 0.2);
      break;
    }

    // ── Rocket Pod: cluster of small missile tubes ──
    case ComponentType.ROCKET_POD: {
      const tubeW = s * 0.22;
      const tubeH = s * 0.12;
      const gap = s * 0.04;
      // 3 tubes stacked vertically (top, center, bottom)
      for (let row = -1; row <= 1; row++) {
        const ty = row * (tubeH + gap);
        gfx.fillStyle(color, 0.85);
        gfx.fillRect(-tubeW * 0.5, ty - tubeH * 0.5, tubeW, tubeH);
        // Dark opening at front (right side)
        gfx.fillStyle(0x331100, 0.8);
        gfx.fillCircle(tubeW * 0.5, ty, tubeH * 0.35);
      }
      // Mounting bracket on left
      gfx.lineStyle(lw * 1.5, color, 0.5);
      gfx.beginPath();
      gfx.moveTo(-tubeW * 0.5, -(tubeH + gap) * 1.2);
      gfx.lineTo(-tubeW * 0.5, (tubeH + gap) * 1.2);
      gfx.strokePath();
      break;
    }

    // ── Jumper: spring coil with base plate ──
    case ComponentType.JUMPER: {
      const springW = s * 0.28;
      const springH = s * 0.35;
      const coils = 4;
      // Base plate (bottom)
      gfx.fillStyle(color, 0.7);
      gfx.fillRect(-springW, springH * 0.6, springW * 2, s * 0.08);
      // Top plate
      gfx.fillRect(-springW * 0.7, -springH * 0.7, springW * 1.4, s * 0.08);
      // Spring coils (zigzag)
      gfx.lineStyle(lw * 1.8, color, 0.9);
      gfx.beginPath();
      const coilStep = (springH * 1.2) / coils;
      let cy = -springH * 0.6;
      gfx.moveTo(-springW * 0.5, cy);
      for (let i = 0; i < coils; i++) {
        cy += coilStep * 0.5;
        gfx.lineTo(springW * 0.5, cy);
        cy += coilStep * 0.5;
        gfx.lineTo(-springW * 0.5, cy);
      }
      gfx.strokePath();
      // Center accent dot
      gfx.fillStyle(0x88ffcc, 0.8);
      gfx.fillCircle(0, 0, s * 0.06);
      break;
    }

    // ── Excavator: pickaxe / claw symbol ──
    case ComponentType.EXCAVATOR: {
      const armLen = s * 0.3;
      // Handle (diagonal line)
      gfx.lineStyle(lw * 2, color, 0.9);
      gfx.beginPath();
      gfx.moveTo(-armLen * 0.6, armLen * 0.6);
      gfx.lineTo(armLen * 0.4, -armLen * 0.4);
      gfx.strokePath();
      // Pick head (arc)
      gfx.lineStyle(lw * 2.5, color, 0.85);
      gfx.beginPath();
      gfx.arc(armLen * 0.1, -armLen * 0.1, armLen * 0.5, -Math.PI * 0.8, -Math.PI * 0.15);
      gfx.strokePath();
      // Accent dot (mineral)
      gfx.fillStyle(0x44ee88, 0.8);
      gfx.fillCircle(-armLen * 0.3, armLen * 0.1, s * 0.06);
      break;
    }

    // ── Camouflage: concentric fading rings + eye symbol ──
    case ComponentType.CAMOUFLAGE: {
      // Outer shimmer rings (fading outward)
      for (let i = 3; i >= 1; i--) {
        const r = s * 0.12 * i;
        gfx.lineStyle(lw, color, 0.2 + i * 0.1);
        gfx.strokeCircle(0, 0, r);
      }
      // Center eye shape (two arcs)
      const ew = s * 0.28;
      const eh = s * 0.14;
      gfx.lineStyle(lw * 1.5, color, 0.85);
      gfx.beginPath();
      gfx.arc(0, eh * 0.4, ew, -Math.PI * 0.85, -Math.PI * 0.15);
      gfx.strokePath();
      gfx.beginPath();
      gfx.arc(0, -eh * 0.4, ew, Math.PI * 0.15, Math.PI * 0.85);
      gfx.strokePath();
      // Pupil
      gfx.fillStyle(color, 0.9);
      gfx.fillCircle(0, 0, s * 0.05);
      // Diagonal shimmer lines
      gfx.lineStyle(lw * 0.5, 0xffffff, 0.15);
      for (let i = -hs; i < hs; i += 5 * k) {
        gfx.lineBetween(i, -hs, i + hs * 0.5, hs);
      }
      break;
    }

    case ComponentType.DIRECTION: {
      // Rightward-pointing arrow (rotation handled by caller)
      const shaft = s * 0.35;
      const headW = s * 0.25;
      const headH = s * 0.35;
      // Shaft
      gfx.fillStyle(color, 0.9);
      gfx.fillRect(-shaft, -s * 0.06, shaft * 1.5, s * 0.12);
      // Arrowhead triangle pointing right
      gfx.fillTriangle(shaft * 0.5, -headH, shaft * 0.5, headH, shaft * 0.5 + headW, 0);
      // Small circle at base
      gfx.fillStyle(color, 0.5);
      gfx.fillCircle(-shaft, 0, s * 0.06);
      break;
    }
  }
}

/**
 * Draw a mini robot preview from a list of design components.
 * The preview is scaled to fit within `maxSize` pixels, centered at (0,0).
 * Returns a Container of individual Graphics objects (renderer-agnostic).
 */
export function drawRobotPreview(
  scene: Phaser.Scene,
  components: { type: ComponentType; lx: number; ly: number; rotation?: number; evoLevel?: number }[],
  maxSize: number,
): Phaser.GameObjects.Container {
  const container = scene.add.container(0, 0);
  if (components.length === 0) return container;

  // Compute bounding box of the design
  let minX = Infinity, maxX = -Infinity, minY = Infinity, maxY = -Infinity;
  for (const c of components) {
    minX = Math.min(minX, c.lx);
    maxX = Math.max(maxX, c.lx);
    minY = Math.min(minY, c.ly);
    maxY = Math.max(maxY, c.ly);
  }

  const designW = maxX - minX + 16; // add one cell width
  const designH = maxY - minY + 16;
  const scale = Math.min(1, maxSize / Math.max(designW, designH));
  const cx = (minX + maxX) / 2;
  const cy = (minY + maxY) / 2;

  const iconSize = Math.max(6, 14 * scale);

  for (const comp of components) {
    const def = COMPONENT_DEFS[comp.type];
    const px = (comp.lx - cx) * scale;
    const py = (comp.ly - cy) * scale;

    const visual = createComponentVisual(scene, comp.type, iconSize, def.color, comp.evoLevel);
    visual.setPosition(px, py);
    if (comp.rotation) visual.setRotation(comp.rotation);
    container.add(visual);
  }

  return container;
}

/**
 * Mapping from ComponentType to its sprite texture keys per evolution level.
 * Only types with actual image assets are listed here.
 * Components not in this map will use the Graphics fallback.
 */
const COMPONENT_TEXTURE_LEVELS: Partial<Record<ComponentType, string[]>> = {
  [ComponentType.STRUCTURE]: ['comp_structure_0', 'comp_structure_1', 'comp_structure_2'],
  [ComponentType.ARMOR]: ['comp_armor_0', 'comp_armor_1', 'comp_armor_3'],
  [ComponentType.BATTERY]: ['comp_battery_0', 'comp_battery_1', 'comp_battery_2'],
  [ComponentType.DIRECTION]: ['comp_direction_0'],
  [ComponentType.STRUCTURE_TRIANGLE]: ['comp_bracket_0', 'comp_bracket_1', 'comp_bracket_2'],
  [ComponentType.WHEEL]: ['comp_wheel_0', 'comp_wheel_1', 'comp_wheel_2', 'comp_wheel_3', 'comp_wheel_4'],
  [ComponentType.STAB_LEGS]: ['comp_stab_0', 'comp_stab_1', 'comp_stab_2'],
  [ComponentType.THRUSTER]: ['comp_thruster_0', 'comp_thruster_1', 'comp_thruster_3'],
  [ComponentType.WING]: ['comp_wing_0', 'comp_wing_1', 'comp_wing_2'],
  [ComponentType.SENSOR_SHORT]: ['comp_radar_short_0', 'comp_radar_short_1'],
  [ComponentType.SENSOR_MEDIUM]: ['comp_radar_medium_0', 'comp_radar_medium_1'],
  [ComponentType.REPAIR_MODULE]: ['comp_repair_0', 'comp_repair_1', 'comp_repair_2'],
  [ComponentType.ROCKET_POD]: ['comp_rocket_pod_0', 'comp_rocket_pod_1', 'comp_rocket_pod_2'],
  [ComponentType.CANNON_LIGHT]: ['comp_cannon_light_0', 'comp_cannon_light_1', 'comp_cannon_light_2'],
  [ComponentType.CANNON_HEAVY]: ['comp_cannon_heavy_0', 'comp_cannon_heavy_1', 'comp_cannon_heavy_2'],
  [ComponentType.EXCAVATOR]: ['comp_excavator_0', 'comp_excavator_1', 'comp_excavator_2'],
  [ComponentType.JUMPER]: ['comp_jumper_0', 'comp_jumper_1', 'comp_jumper_2'],
  [ComponentType.LASER]: ['comp_laser_0', 'comp_laser_1', 'comp_laser_2'],
  [ComponentType.BURST_CANNON]: ['comp_burst_cannon_0', 'comp_burst_cannon_1', 'comp_burst_cannon_2'],
  [ComponentType.MISSILE_SAM]: ['comp_aa_missile_0', 'comp_aa_missile_1', 'comp_aa_missile_2'],
  [ComponentType.MISSILE_SOL_SOL]: ['comp_ground_missile_0', 'comp_ground_missile_1', 'comp_ground_missile_2'],
  [ComponentType.MINE_LAYER]: ['comp_mine_layer_0', 'comp_mine_layer_1', 'comp_mine_layer_2'],
  [ComponentType.RECHARGE_MODULE]: ['comp_recharge_0', 'comp_recharge_1', 'comp_recharge_2'],
  [ComponentType.EXPLOSIVE_CHARGE]: ['comp_explosive_0', 'comp_explosive_1', 'comp_explosive_2', 'comp_explosive_3'],
  [ComponentType.FLAMETHROWER]: ['comp_flamethrower_0', 'comp_flamethrower_1', 'comp_flamethrower_2'],
  [ComponentType.MUD_LAUNCHER]: ['comp_mud_catapult'],
};

/** Returns the texture key for a component type at its current evolution level. */
export function getComponentTextureKey(type: ComponentType): string | undefined {
  const keys = COMPONENT_TEXTURE_LEVELS[type];
  if (!keys || keys.length === 0) return undefined;
  const level = ProgressionManager.getComponentLevel(type);
  const idx = Math.min(level, keys.length - 1);
  return keys[idx];
}

/** Returns the texture key for a component type at a specific level. */
export function getComponentTextureKeyAtLevel(type: ComponentType, level: number): string | undefined {
  const keys = COMPONENT_TEXTURE_LEVELS[type];
  if (!keys || keys.length === 0) return undefined;
  const idx = Math.min(level, keys.length - 1);
  return keys[idx];
}

/** Union type for component visuals (sprite-based or Graphics fallback) */
export type ComponentVisual = Phaser.GameObjects.Graphics | Phaser.GameObjects.Sprite;

/**
 * Create a visual for a component — uses sprite image if available,
 * otherwise falls back to Graphics primitive drawing.
 * Returns a Sprite or Graphics that can be positioned/rotated.
 * The visual is centered at (0,0) and sized to `size` pixels.
 */
export function createComponentVisual(
  scene: Phaser.Scene,
  type: ComponentType,
  size: number,
  color: number,
  evoLevel?: number,
): ComponentVisual {
  const textureKey = evoLevel !== undefined ? getComponentTextureKeyAtLevel(type, evoLevel) : getComponentTextureKey(type);
  let visual: ComponentVisual;
  if (textureKey && scene.textures.exists(textureKey)) {
    const sprite = scene.add.sprite(0, 0, textureKey);
    // Scale image down to target size
    const texW = sprite.width;
    const texH = sprite.height;
    const scale = size / Math.max(texW, texH);
    sprite.setScale(scale);
    visual = sprite;
  } else {
    // Fallback: render Graphics icon into a cached texture, then use a batchable Sprite.
    // This converts N separate GPU draw calls into 1 batched draw call for all same-type components.
    const genKey = `comp_gen_${type}_${size}`;
    if (!scene.textures.exists(genKey)) {
      const pad = 4;
      const texSize = size + pad * 2;
      const rt = makeRenderTexture(scene, texSize, texSize);
      const gfx = scene.make.graphics({});
      drawComponentIcon(gfx, type, size, color);
      rt.draw(gfx, texSize / 2, texSize / 2);
      rt.saveTexture(genKey);
      rt.destroy();
      gfx.destroy();
    }
    const sprite = scene.add.sprite(0, 0, genKey);
    visual = sprite;
  }

  // Apply visual scale overrides from COMP GOD (per-level override).
  // NOTE: offsetX/offsetY and defaultRotation are NOT applied here —
  // callers handle positioning (rotation-aware offset) and rotation composition.
  // Read overrides directly (not def.*) to avoid double-apply with applyOverrideToDef mutation.
  const level = evoLevel ?? ProgressionManager.getComponentLevel(type);
  const ov = getOverride(type, level);
  const sx = ov?.scaleX ?? 1;
  const sy = ov?.scaleY ?? 1;
  if (sx !== 1 || sy !== 1) {
    visual.setScale(visual.scaleX * sx, visual.scaleY * sy);
  }

  return visual;
}

/** Category color bands for palette grouping */
export const CATEGORY_COLORS: Record<string, number> = {
  passive: 0x666688,
  movement: 0x44aa44,
  sensor: 0x44cc44,
  weapon: 0xcc4444,
  support: 0x8844cc,
};
